import { pmNewBlock } from './pmBlocks';
import { PM_ORG_LOGO_TOKEN } from './pmDesign';

const t = pmNewBlock;

// B2B Hero brand templates
//
// Org-related data (sender address, phone, email, website, company name)
// is expressed as {{org.*}} merge tokens. The editor resolves these against the
// current org's companyProfile at render time so the user sees a realistic
// preview while the underlying designData stays org-agnostic and reusable.
// Marketing copy (headings, body text, voucher codes, CTAs) stays as plain
// text — it's intentional content, not org identity.

const ORG_SENDER_LINE = '{{org.name}} · {{org.street}} · {{org.postal}} {{org.city}}';

// Recipient address window lines for the starter templates. The window is fixed/read-only and
// always holds the standard recipient merge tags — the backend resolves them per recipient at
// send/preview time. Mirrors PM_BLOCK_DEFAULTS.address (single source for the canonical tags).
const RECIPIENT_ADDRESS_LINES = [
	'{{firmenname}}',
	'{{anrede}} {{vorname}} {{nachname}}',
	'{{straße}}',
	'{{plz}} {{ort}}',
];

export const TPL_B2B_SIMPLE = {
	id: 'b2b-simple',
	name: 'B2B Hero — Schlichter Brief',
	desc: 'Klassischer B2B-Hero-Brief mit Marken-Header und Kontakt-Fußleiste. Perfekt für persönliche Erstansprache.',
	layout: 'simple',
	hasFooter: true,
	theme: {
		primary: '#5b5bf5',
		primaryDark: '#1f1f4e',
		onPrimary: '#ffffff',
		soft: '#e9eaff',
		fontKey: 'inter',
		textSize: 'medium',
		paperBg: '#ffffff',
	},
	logo: { src: PM_ORG_LOGO_TOKEN, align: 'right', show: true },
	zones: {
		sender: [t('senderLine', { text: ORG_SENDER_LINE })],
		address: [t('address', { lines: RECIPIENT_ADDRESS_LINES })],
		body: [
			t('date', { text: '{{org.city}}, {{datum}}', align: 'left' }),
			t('heading', { text: 'Volle KI-Power für Ihren Vertrieb', level: 2, align: 'center', color: 'primary' }),
			t('spacer', { height: 4 }),
			t('salutation', { text: 'Sehr geehrte Damen und Herren,' }),
			t('paragraph', {
				text: 'die Suche nach neuen Kunden kostet Ihr Team Woche für Woche viele Stunden – Recherche bei Google, veraltete Listen, falsche Ansprechpartner. B2B Hero, die deutsche Vertriebssoftware aus Braunschweig, macht damit Schluss.',
			}),
			t('paragraph', {
				text: 'Unsere KI findet automatisch genau die Firmen und Entscheider, die wirklich zu Ihnen passen:',
			}),
			t('container', {
				bg: 'soft',
				children: [
					t('bullets', {
						title: '',
						marker: 'check',
						colorMode: null,
						items: [
							'Passende Firmen per KI – Branche und Region wählen, fertige Lead-Liste mit Relevanz-Bewertung erhalten.',
							'Geprüfte Ansprechpartner – aktuelle E-Mail-Adressen und Telefonnummern der richtigen Entscheider.',
						],
					}),
				],
			}),
			t('paragraph', {
				text: 'Anschließend kontaktieren Sie Ihre Wunschkunden direkt aus der Software – per automatisierter E-Mail-Kampagne, telefonisch oder postalisch, mit KI-Unterstützung beim Texten und 100 % DSGVO-konform.',
			}),
			t('spacer', { height: 6 }),
			t('paragraph', { text: 'Über ein kurzes Gespräch freue ich mich. Mit freundlichen Grüßen' }),
			t('personCard', {
				name: 'Vorname Nachname',
				role: 'Vorname Nachname · Vertrieb & Beratung',
				company: '{{org.name}}',
			}),
		],
		sidebar: [],
		footer: [
			t('contact', {
				rows: [
					{
						icon: 'building',
						label: '{{org.name}}',
						value: '{{org.street}}\n{{org.postal}} {{org.city}}',
					},
					{
						icon: 'mail',
						label: '{{org.email}}',
						value: '{{org.phone}} · {{org.website}}',
					},
				],
			}),
		],
	},
};

export const TPL_B2B_OFFER = {
	id: 'b2b-offer',
	name: 'B2B Hero — Angebot mit Vorteilsbox',
	desc: 'Sales-Mailing mit oberer Aktions-Leiste, Adresskopf, Vorteils-Sidebar, Gutschein-Karte und Pflichthinweis. Für gezielte Conversion-Mailings.',
	layout: 'sidebar',
	hasFooter: false,
	theme: {
		primary: '#5b5bf5',
		primaryDark: '#1f1f4e',
		onPrimary: '#ffffff',
		soft: '#e9eaff',
		fontKey: 'inter',
		textSize: 'medium',
		paperBg: '#ffffff',
		sidebarFrom: '#ffffff',
		sidebarTo: '#ffffff',
		sidebarText: '#15163e',
		decorations: {
			topBar: { text: 'Testen Sie 50 Kontakte kostenlos – ohne Kreditkarte' },
		},
	},
	logo: { src: PM_ORG_LOGO_TOKEN, align: 'right', show: true },
	zones: {
		sender: [t('senderLine', { text: ORG_SENDER_LINE })],
		address: [t('address', { lines: RECIPIENT_ADDRESS_LINES })],
		body: [
			t('heading', { text: 'Unser Angebot für Sie!', level: 1, color: '#111' }),
			t('salutation', { text: 'Sehr geehrte Damen und Herren,' }),
			t('paragraph', {
				text: 'wie viele Stunden investiert Ihr Vertrieb jede Woche in die Recherche nach neuen Kunden? Mit B2B Hero sind es ab sofort Minuten.',
			}),
			t('paragraph', {
				text: 'Sie wählen Branche und Region – unsere KI erstellt automatisch eine vollständige Liste passender Unternehmen, bewertet jeden Lead in Prozent und liefert die geprüften Kontaktdaten der richtigen Ansprechpartner gleich mit.',
			}),
			t('paragraph', {
				text: 'Kontaktieren Sie Ihre Wunschkunden anschließend direkt aus der Software – per automatisierter E-Mail-Sequenz, Telefon oder Post. Alles an einem Ort und 100 % DSGVO-konform, made in Germany.',
			}),
			t('spacer', { height: 16 }),
			t('callToAction', {
				title: 'Jetzt kostenlos starten – 50 Kontakte gratis',
				subtitle: '{{org.website}} · Tel. {{org.phone}} · ohne Kreditkarte',
			}),
			t('spacer', { height: 16 }),
			t('personCard', {
				name: 'Vorname Nachname',
				role: 'Vorname Nachname · Vertrieb & Beratung',
				company: '{{org.name}}',
			}),
		],
		sidebar: [
			t('voucherBox', {
				topline: '',
				bigNumber: '50',
				bigLabel: 'Kontakte',
				smallLabel: 'gratis',
				subtitle: 'Ihr persönliches Startguthaben',
				codeLabel: '',
				code: 'B2B-HERO',
				footnote: 'Ohne Kreditkarte · ohne Verpflichtung',
			}),
		],
		footer: [],
	},
};

export const TPL_B2B_VOUCHER = {
	id: 'b2b-voucher',
	name: 'B2B Hero — Gutschein-Mailing',
	desc: 'Premium-Mailing mit dunkler Sidebar voller Gutschein, Schritt-für-Schritt-Anleitung, Icon-Features und Trust-Badge. Maximale Aufmerksamkeit für Top-Leads.',
	layout: 'coloredSidebar',
	hasFooter: false,
	theme: {
		primary: '#5b5bf5',
		primaryDark: '#1f1f4e',
		onPrimary: '#ffffff',
		soft: '#e9eaff',
		fontKey: 'inter',
		textSize: 'medium',
		paperBg: '#ffffff',
		sidebarFrom: '#4b4ce1',
		sidebarTo: '#15163e',
		sidebarText: '#ffffff',
	},
	logo: { src: PM_ORG_LOGO_TOKEN, align: 'left', show: true },
	zones: {
		sender: [t('senderLine', { text: ORG_SENDER_LINE })],
		address: [t('address', { lines: RECIPIENT_ADDRESS_LINES })],
		body: [
			t('heading', { text: 'Neue Kunden finden?\nDas erledigt ab jetzt die KI.', level: 1, color: 'primary' }),
			t('paragraph', {
				text: 'Sichern Sie sich jetzt 50 geprüfte B2B-Kontakte gratis – ohne Kreditkarte, ohne Verpflichtung.',
			}),
			t('salutation', { text: 'Hallo Herr/Frau [Nachname],' }),
			t('paragraph', {
				text: 'Recherche bei Google, veraltete Listen, falsche Ansprechpartner – die Suche nach neuen Kunden kostet Ihr Team Woche für Woche viele Stunden. B2B Hero macht damit Schluss.',
			}),
			t('paragraph', {
				text: 'Sie wählen Branche und Region, unsere KI erstellt automatisch eine Liste passender Firmen, bewertet jeden Lead und liefert die geprüften Kontaktdaten der richtigen Entscheider gleich mit. Anschließend kontaktieren Sie Ihre Wunschkunden direkt aus der Software – per E-Mail-Kampagne, Telefon oder Post.',
			}),
			t('paragraph', {
				text: 'Über 1.000 Unternehmen aus allen Branchen gewinnen mit B2B Hero bereits planbar neue Kunden. Lösen Sie Ihren Code einfach unter {{org.website}} ein und legen Sie los.',
			}),
			t('heading', { text: 'Ihr {{org.name}} Team', level: 2, color: 'primary' }),
		],
		sidebar: [
			t('voucherBox', {
				topline: 'Jetzt einlösen',
				bigNumber: '50',
				bigLabel: 'Kontakte',
				smallLabel: 'Gratis',
				subtitle: '+ kostenlose KI-Analyse Ihrer Leads',
				codeLabel: 'Ihr persönlicher Code',
				code: 'B2B-HERO-50',
				footnote: 'gültig: UNBEGRENZT · ohne Kreditkarte',
			}),
			t('stepsList', {
				title: 'So sichern Sie sich Ihr Guthaben:',
				items: [
					'Auf {{org.website}} kostenlos registrieren',
					'Branche & Region wählen – die KI findet passende Firmen',
					'Code eingeben & 50 Kontakte gratis sichern',
				],
			}),
		],
		footer: [],
	},
};

export const PM_TEMPLATES = [TPL_B2B_SIMPLE, TPL_B2B_OFFER, TPL_B2B_VOUCHER];

export function pmClone(tpl) {
	return JSON.parse(JSON.stringify(tpl));
}

/**
 * Factory for a PDF-upload template. The user brings a finished A4 design as PDF sheets (each sheet =
 * front + optional back); the backend only stamps the recipient address into the fixed DIN window on
 * the first sheet. `kind: 'pdf'` discriminates this from the block model everywhere (editor branch,
 * preview, send). Every sheet's front is required before sending; asset ids start null (filled in the
 * setup view after upload).
 * @returns {Object}
 */
export function pmPdfTemplate() {
	return {
		kind: 'pdf',
		name: 'PDF-Briefvorlage',
		pdf: {
			sheets: [{ frontAssetId: null, backAssetId: null, frontMeta: null, backMeta: null }],
			whiteBackdrop: true,
		},
	};
}
